import { ApiCallError } from "Infrastructure/Api/ApiUtils";
import type { ResourceDictionary } from "Infrastructure/Translations/ResourceDictionary";
import {
	type ObjPathProxy,
	Resources,
	useResource,
	useServerError,
} from "Infrastructure/Translations/Resources";
import { useEffect, useRef } from "react";
import type { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";
import { toast } from "sonner";

type FeatureErrorResources = ObjPathProxy<
	ResourceDictionary,
	{ General: string }
>;

type UseHandleErrorsProps<T extends FieldValues> = {
	// `unknown` instead of `Error` because TanStack Query's TError is whatever the
	// query/mutation declared. Hey API types it as the response body shape, while
	// the runtime is always `ApiCallError` thanks to `apiInterceptors`. The hook
	// guards on `instanceof ApiCallError` internally.
	error: unknown;
	form: UseFormReturn<T>;
	formKey: FieldPath<T>;
	resource: FeatureErrorResources;
};

const useHandleServerError = <T extends FieldValues>({
	error,
	formKey,
	form,
	resource,
}: UseHandleErrorsProps<T>) => {
	const errorKeyRef = useRef<(FieldPath<T> | null)[]>([]);
	const prevServerErrorRef = useRef<unknown>(undefined);
	const { t } = useResource();
	const { translateError } = useServerError(resource, resource.General);

	// React only when `error` identity changes. The form/translation closures are
	// captured from the latest render but only used when `error` flips — they
	// don't need to be in deps (and shouldn't be: putting `formState.errors` or
	// `watch()` keys here would re-fire the effect on every keystroke).
	useEffect(() => {
		if (prevServerErrorRef.current === error) return;
		prevServerErrorRef.current = error;

		// Clear previously set server errors regardless of branch.
		for (const k of errorKeyRef.current) {
			if (k) form.clearErrors(k);
		}
		errorKeyRef.current = [];

		if (!error) return;

		const formKeys = Object.keys(form.getValues());
		const previousErrors = form.formState.errors;
		const errorMessages = transformMessages(t, error, formKey, translateError);

		if (errorMessages.length === 0) {
			form.setError(formKey, {
				type: "server",
				message: t(Resources.Errors.ApplicationError.Title),
			});
			return;
		}

		for (const { messages, key: errorKey } of errorMessages) {
			const message = messages.join("\n");
			const k = (errorKey ?? formKey) as FieldPath<T>;

			if (!formKeys.includes(k)) {
				toast.error(message, { duration: Number.POSITIVE_INFINITY });
				continue;
			}

			const prevMessage = previousErrors[k]?.message as string | undefined;
			form.setError(k, {
				type: "server",
				message:
					prevMessage && prevMessage !== message
						? `${prevMessage} ${message}`
						: message,
			});
			errorKeyRef.current.push(k);
		}
	}, [error, form, formKey, t, translateError]);
};

const transformMessages = (
	t: ReturnType<typeof useResource>["t"],
	error: unknown,
	formKey: FieldPath<FieldValues>,
	translateError: ReturnType<typeof useServerError>["translateError"],
) => {
	const fallbackErrorMessage = [
		{
			key: formKey,
			messages: [t(Resources.Errors.ApplicationError.Title)],
		},
	];

	if (error instanceof ApiCallError && !!error.body) {
		const exception = error.body;

		if (!!exception.errors && Object.keys(exception.errors).length > 0) {
			const errorMessages = Object.entries(exception.errors).map(
				([key, value]) => {
					const messages = value
						.map(
							(v) =>
								translateError(
									v.reason,
									v.params as Record<string, unknown> | null | undefined,
								) ?? v.message,
						)
						.filter((v): v is string => Boolean(v));

					return {
						key,
						messages,
					};
				},
			);

			return errorMessages.length > 0 ? errorMessages : fallbackErrorMessage;
		}
	}
	return fallbackErrorMessage;
};

export default useHandleServerError;
